/**
 * useExportDrilldownPDF.ts
 * ─────────────────────────────────────────────────────────────────────────────
 * Hook d'export PDF spécifique au drilldown "Détail par Dépôt" — Nawara BI
 *
 * Corrections v2 :
 *  1. Logo : fetch de l'image via URL relative et conversion en base64
 *  2. Chiffres : suppression des espaces insécables (U+202F) de Intl.NumberFormat
 *     → remplacement par une fonction de formatage sans séparateur d'espace
 *  3. Total : alignement correct, cellWidth cohérent avec le reste du tableau
 *
 * Structure du PDF A4 paysage :
 *  • Page 1 : En-tête violet + cercle logo centré + tableau dépôts × mesures + pied de page
 *
 * Dépendances : jspdf + jspdf-autotable
 *   npm install jspdf jspdf-autotable
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { useCallback } from "react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

// ── Types ──────────────────────────────────────────────────────────────────────

export interface DrilldownPDFRow {
  /** Nom du dépôt */
  depot: string;
  /** Valeurs dans l'ordre de `headers` */
  values: number[];
  coutTTC?: number | null;
  marge?: number | null;
}

export interface DrilldownPDFOptions {
  filename?: string;
  /** Nom de la catégorie cliquée, ex : "DAME" */
  categorie: string;
  /** Headers des mesures (sans "Dépôt") */
  headers: string[];
  rows: DrilldownPDFRow[];
  grandTotals: number[];
  totalCoutTTC?: number | null;
  totalMarge?: number | null;
  hasCoutTTC?: boolean;
  hasMarge?: boolean;
  dateFrom?: string;
  dateTo?: string;
  selectedMagasin?: string | null;
  soldeCode?: string | null;
  demarquePhase?: string | null;
  /**
   * Logo en base64 (PNG ou JPG).
   * Format : "data:image/png;base64,iVBOR..." ou juste la chaîne base64 brute.
   * Peut aussi être une URL relative (ex: "/nawara.jpeg") — sera fetchée automatiquement.
   * Si absent ou invalide, les initiales "NB" (Nawara BI) sont affichées dans le cercle.
   */
  logoBase64?: string | null;
}

// ── Palette ────────────────────────────────────────────────────────────────────

const C = {
  violet:      [24, 75, 140] as [number, number, number],
  violetDark:  [13, 42, 80] as [number, number, number],
  violetMed:   [29, 91, 168] as [number, number, number],
  violetLight: [238, 244, 251] as [number, number, number],
  rose:        [125, 168, 217] as [number, number, number],
  roseMed:     [215, 229, 245] as [number, number, number],
  roseDark:    [74, 134, 199] as [number, number, number],
  teal:        [0, 160, 140] as [number, number, number],
  tealLight:   [211, 239, 234] as [number, number, number],
  orange:      [176, 115, 3] as [number, number, number],
  indigo:      [29, 91, 168] as [number, number, number],
  indigoDark:  [19, 59, 110] as [number, number, number],
  indigoLight: [215, 229, 245] as [number, number, number],
  indigoAlt:   [215, 229, 245] as [number, number, number],
  success:     [0, 160, 140] as [number, number, number],
  danger:      [214, 69, 69] as [number, number, number],
  white:       [255, 255, 255] as [number, number, number],
  gray50:      [248, 250, 252] as [number, number, number],
  gray200:     [226, 232, 240] as [number, number, number],
  gray500:     [100, 116, 139] as [number, number, number],
  gray700:     [51, 65, 85] as [number, number, number],
  gray800:     [30, 41, 59] as [number, number, number],
};

// ── Filtre valeurs indésirables ──────────────────────────────────────────────
const INVALID_DIM_RE = /^\s*(all|unknown|couleur inconnue|inconnue|non renseign[ée]|n\/a|na|null|undefined|#n\/a)\s*$/i;
/** Retourne false si la valeur est vide, "all", "Unknown", "inconnue", etc. */
function isValidDimension(v: string | null | undefined): boolean {
  if (!v || v.trim() === "") return false;
  return !INVALID_DIM_RE.test(v.trim());
}

// ── Helpers ────────────────────────────────────────────────────────────────────

/**
 * FIX #2 — Formatage des nombres SANS espaces insécables (U+202F / U+00A0).
 * Intl.NumberFormat "fr-FR" utilise U+202F comme séparateur de milliers,
 * ce qui provoque l'espacement des chiffres dans jsPDF.
 * On remplace tous les espaces Unicode par rien (pas de séparateur de milliers)
 * ou par un espace ASCII normal selon la préférence.
 *
 * Ici on choisit le point comme séparateur de milliers et la virgule pour les décimales,
 * ce qui est lisible et ne pose aucun problème de rendu dans jsPDF.
 */
function fmt(n: number | null | undefined): string {
  if (n == null || isNaN(n as number)) return "-";
  const abs = Math.abs(n as number);
  // Formater manuellement : partie entière avec séparateur point, décimales avec virgule
  const fixed = abs.toFixed(2);
  const [intPart, decPart] = fixed.split(".");
  // Ajouter séparateurs de milliers (point) — ASCII standard, pas d'espace
  const intFormatted = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return `${intFormatted},${decPart}`;
}

/** Formate avec signe moins si négatif */
function fmtSigned(n: number | null | undefined): string {
  if (n == null || isNaN(n as number)) return "-";
  return ((n as number) >= 0 ? "" : "-") + fmt(n);
}

/**
 * FIX #1 — Chargement du logo depuis une URL relative ou une chaîne base64.
 * Retourne une chaîne "data:image/...;base64,..." prête pour jsPDF,
 * ou null si le chargement échoue.
 */
async function resolveLogoToBase64(logoSrc: string): Promise<string | null> {
  // Déjà en base64 inline
  if (logoSrc.startsWith("data:")) return logoSrc;

  // Chaîne base64 brute (sans préfixe data:)
  if (!logoSrc.startsWith("/") && !logoSrc.startsWith("http")) {
    return `data:image/png;base64,${logoSrc}`;
  }

  // URL relative ou absolue → fetch et conversion
  try {
    const response = await fetch(logoSrc);
    if (!response.ok) return null;
    const blob = await response.blob();
    return await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

// ── Page unique : en-tête + cercle logo + tableau ──────────────────────────────

async function buildPage(
  doc: jsPDF,
  opts: DrilldownPDFOptions,
  logoDataUrl: string | null
): Promise<void> {
  const pw = doc.internal.pageSize.getWidth();   // 297 mm en paysage A4
  const ph = doc.internal.pageSize.getHeight();  // 210 mm en paysage A4

  // ══════════════════════════════════════════════════════════════════
  // SECTION 1 — BANDE D'EN-TÊTE VIOLET FONCÉ
  // ══════════════════════════════════════════════════════════════════
  const headerH = 22;

  // Fond principal violet foncé
  doc.setFillColor(...C.violetDark);
  doc.rect(0, 0, pw, headerH, "F");

  // Liseré rose en haut (2 mm)
  doc.setFillColor(...C.rose);
  doc.rect(0, 0, pw, 2, "F");

  // Texte gauche : titre
  doc.setTextColor(...C.white);
  doc.setFontSize(13);
  doc.setFont("helvetica", "bold");
  // FIX : setCharSpace(0) pour éviter tout espacement indésirable
  doc.setCharSpace(0);
  doc.text("Detail par Depot", 10, 14);

  // Texte centre : catégorie
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...C.roseMed);
  doc.text(`Categorie : ${opts.categorie}`, pw / 2, 14, { align: "center" });

  // Texte droite : date
  doc.setTextColor(200, 200, 200);
  doc.setFontSize(8);
  doc.text(
    `Genere le ${new Date().toLocaleString("fr-FR")}`,
    pw - 8,
    14,
    { align: "right" }
  );

  // ══════════════════════════════════════════════════════════════════
  // SECTION 2 — CERCLE LOGO centré juste sous l'en-tête
  // ══════════════════════════════════════════════════════════════════
  const circleR  = 12;   // rayon en mm
  const circleCX = pw / 2;
  const circleCY = headerH + 2 + circleR; // 2 mm de marge sous l'en-tête

  // Cercle de fond (violet)
  doc.setFillColor(...C.violet);
  doc.setDrawColor(...C.roseMed);
  doc.setLineWidth(0.8);
  doc.circle(circleCX, circleCY, circleR, "FD");

  // Anneau rose interne (effet décoratif)
  doc.setDrawColor(...C.rose);
  doc.setLineWidth(0.4);
  doc.circle(circleCX, circleCY, circleR - 1.5, "S");

  // FIX #1 — Affichage du logo si disponible
  if (logoDataUrl) {
    try {
      const logoSize = (circleR - 2) * 2;
      const format = logoDataUrl.includes("image/jpeg") ? "JPEG" : "PNG";

      // Clipper l'image dans le cercle via canvas HTML pour éviter le débordement carré
      const canvas = document.createElement("canvas");
      const diameter = 200; // résolution du canvas en px
      canvas.width = diameter;
      canvas.height = diameter;
      const ctx = canvas.getContext("2d")!;

      // Charger l'image dans le canvas
      await new Promise<void>((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
          // Tracer un cercle de clipping
          ctx.beginPath();
          ctx.arc(diameter / 2, diameter / 2, diameter / 2, 0, Math.PI * 2);
          ctx.closePath();
          ctx.clip();
          // Dessiner l'image dans ce cercle
          ctx.drawImage(img, 0, 0, diameter, diameter);
          resolve();
        };
        img.onerror = reject;
        img.src = logoDataUrl!;
      });

      const clippedDataUrl = canvas.toDataURL("image/png");
      doc.addImage(
        clippedDataUrl,
        "PNG",
        circleCX - logoSize / 2,
        circleCY - logoSize / 2,
        logoSize,
        logoSize
      );
    } catch {
      doc.setTextColor(...C.white);
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.setCharSpace(0);
      doc.text("NB", circleCX, circleCY + 4, { align: "center" });
    }
  } else {
    // Initiales "NB"
    doc.setTextColor(...C.white);
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.setCharSpace(0);
    doc.text("NB", circleCX, circleCY + 4, { align: "center" });
  }

  // ══════════════════════════════════════════════════════════════════
  // SECTION 3 — TABLEAU
  // ══════════════════════════════════════════════════════════════════
  const tableStartY = circleCY + circleR + 4; // 4 mm sous le cercle

  // Construction des colonnes
  const allHeaders = ["Depot", ...opts.headers];
  if (opts.hasCoutTTC) allHeaders.push("Cout TTC");
  if (opts.hasMarge)   allHeaders.push("Marge");

  // FIX #3 — Largeur fixe de la colonne Dépôt, largeurs auto pour le reste
  // On calcule une largeur fixe raisonnable selon le nombre de colonnes
  const usableWidth = pw - 20; // marges gauche + droite
  const depotColW = 36;
  const remainingW = usableWidth - depotColW;
  const numDataCols = allHeaders.length - 1;
  const dataColW = numDataCols > 0 ? Math.floor(remainingW / numDataCols) : remainingW;

  // Styles de l'en-tête par colonne
  const headCells = allHeaders.map((h, i) => {
    let bg: [number, number, number] = C.rose;
    if (i === 0)               bg = C.indigoDark;
    else if (h === "Cout TTC") bg = C.teal;
    else if (h === "Marge")    bg = C.orange;
    return {
      content: h,
      styles: {
        fillColor: bg,
        textColor: C.white,
        fontStyle: "bold" as const,
        halign: i === 0 ? ("left" as const) : ("right" as const),
      },
    };
  });

  // Filtrer les dépôts indésirables
  const filteredRows = (opts.rows ?? []).filter(r => isValidDimension(r.depot));

  // Lignes du corps
  const bodyRows = filteredRows.map((row) => {
    const cells: any[] = [row.depot];
    row.values.forEach((v) => cells.push(fmt(v)));
    if (opts.hasCoutTTC) cells.push(fmt(row.coutTTC));
    if (opts.hasMarge) {
      const m = row.marge ?? 0;
      cells.push({
        content: fmtSigned(m),
        styles: { textColor: m >= 0 ? C.success : C.danger, halign: "right" as const },
      });
    }
    return cells;
  });

  // FIX #3 — Ligne total : même nombre de cellules, même largeur de colonnes
  const totalLabel = `TOTAL (${filteredRows.length} depots)`;
  const totalRow: any[] = [
    {
      content: totalLabel,
      styles: {
        fontStyle: "bold" as const,
        textColor: C.white,
        fillColor: C.indigoDark,
        halign: "left" as const,
      },
    },
  ];

  opts.grandTotals.forEach((v) =>
    totalRow.push({
      content: fmt(v),
      styles: {
        fontStyle: "bold" as const,
        textColor: [176, 203, 235] as [number, number, number],
        fillColor: C.indigoDark,
        halign: "right" as const,
      },
    })
  );

  if (opts.hasCoutTTC && opts.totalCoutTTC != null)
    totalRow.push({
      content: fmt(opts.totalCoutTTC),
      styles: {
        fontStyle: "bold" as const,
        textColor: C.white,
        fillColor: C.teal,
        halign: "right" as const,
      },
    });

  if (opts.hasMarge && opts.totalMarge != null) {
    const tm = opts.totalMarge;
    totalRow.push({
      content: fmtSigned(tm),
      styles: {
        fontStyle: "bold" as const,
        textColor: C.white,
        fillColor: tm >= 0 ? C.success : C.danger,
        halign: "right" as const,
      },
    });
  }

  // FIX #3 — Styles de colonnes avec largeurs explicites
  const columnStyles: Record<number, object> = {
    0: { cellWidth: depotColW, halign: "left", fontStyle: "bold" },
  };
  for (let i = 1; i < allHeaders.length; i++) {
    columnStyles[i] = { cellWidth: dataColW, halign: "right" };
  }

  autoTable(doc, {
    startY: tableStartY,
    head: [headCells],
    body: bodyRows,
    foot: [totalRow],
    theme: "grid",
    tableWidth: usableWidth,
    margin: { left: 10, right: 10 },
    styles: {
      fontSize: 8,
      cellPadding: { top: 2.5, right: 5, bottom: 2.5, left: 5 },
      overflow: "linebreak",
      font: "helvetica",
      // FIX #2 — Désactiver le kerning/espacement automatique
      halign: "right",
    },
    headStyles: {
      fillColor: C.rose,
      textColor: C.white,
      fontStyle: "bold",
      fontSize: 8,
    },
    footStyles: {
      fillColor: C.indigoDark,
      textColor: C.white,
      fontStyle: "bold",
    },
    alternateRowStyles: { fillColor: C.gray50 },
    columnStyles,
    didParseCell(data) {
      // Colonne Dépôt corps : fond indigo
      if (data.section === "body" && data.column.index === 0) {
        data.cell.styles.fillColor =
          data.row.index % 2 === 0 ? C.indigoLight : C.indigoAlt;
        data.cell.styles.textColor = C.indigoDark;
        data.cell.styles.fontStyle = "bold";
        data.cell.styles.halign = "left";
      }
      // Colonne Coût TTC corps : texte teal
      const coutIdx = opts.headers.length + 1;
      if (
        data.section === "body" &&
        opts.hasCoutTTC &&
        data.column.index === coutIdx
      ) {
        data.cell.styles.textColor = C.teal;
        data.cell.styles.fontStyle = "bold";
      }
      // Pied de page : s'assurer que la hauteur de ligne est suffisante
      if (data.section === "foot") {
        data.cell.styles.minCellHeight = 10;
      }
    },
    // Pied de page sur chaque page du tableau
    didDrawPage() {
      _drawFooter(doc, pw, ph, opts);
    },
  });

  // Note bas de tableau (formules)
  const finalY: number =
    (doc as any).lastAutoTable?.finalY ?? tableStartY + 30;

  if (opts.hasCoutTTC || opts.hasMarge) {
    const notes: string[] = [];
    if (opts.hasCoutTTC) notes.push("Cout TTC = PUHT Achat + TVA Achat");
    if (opts.hasMarge)   notes.push("Marge = PTTC - Cout TTC");
    doc.setFontSize(7);
    doc.setTextColor(...C.gray500);
    doc.setFont("helvetica", "italic");
    doc.setCharSpace(0);
    doc.text(notes.join("     |     "), 10, finalY + 5);
  }
}

// ── Pied de page ──────────────────────────────────────────────────────────────

function _drawFooter(
  doc: jsPDF,
  pw: number,
  ph: number,
  opts: DrilldownPDFOptions
) {
  // Liseré violet en bas
  doc.setFillColor(...C.violet);
  doc.rect(0, ph - 7, pw, 7, "F");

  doc.setCharSpace(0);

  // Texte gauche
  doc.setFontSize(7.5);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...C.roseMed);
  doc.text("Rapport d'export - Analyse par Categorie", 10, ph - 2.5);

  // Texte centre : date
  doc.setFont("helvetica", "normal");
  doc.setTextColor(220, 200, 255);
  doc.text(new Date().toLocaleString("fr-FR"), pw / 2, ph - 2.5, {
    align: "center",
  });

  // Texte droite : catégorie
  doc.setTextColor(...C.white);
  doc.text(`Categorie : ${opts.categorie}`, pw - 10, ph - 2.5, {
    align: "right",
  });
}

// ── Hook principal ─────────────────────────────────────────────────────────────

export function useExportDrilldownPDF() {
  const exportDrilldownPDF = useCallback(
    async (opts: DrilldownPDFOptions) => {
      const filename =
        opts.filename ??
        `Detail_${opts.categorie}_${new Date().toISOString().slice(0, 10)}`;

      // FIX #1 — Résoudre le logo AVANT de créer le PDF
      // "/nawara.jpeg" sera fetchée et convertie en base64
      let logoDataUrl: string | null = null;
      if (opts.logoBase64) {
        logoDataUrl = await resolveLogoToBase64(opts.logoBase64);
      }

      // A4 paysage
      const doc = new jsPDF({
        orientation: "landscape",
        unit: "mm",
        format: "a4",
      });

      await buildPage(doc, opts, logoDataUrl);

      doc.save(`${filename}.pdf`);
    },
    []
  );

  return { exportDrilldownPDF };
}